import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { toast } from "react-hot-toast";
import { login } from "../../actions/authAction/loginAction";
import OverlayLoading from "../../components/Loading/OverlayLoading";
import ThemeToggle from "../../components/ThemeToggle";
import { getStoreLoginLogo, getStoreName, normalizeStoreSetting } from "../../utils/helpers/storeSetting";
import { getPublicSiteUrl } from "../../utils/helpers/tenantHost";
import { FieldFeedback } from "../../components/FormValidation";
import useFormValidation from "../../hooks/useFormValidation";
import { validationRules as rule } from "../../utils/helpers/formValidation";
import "./login.css";
import AkasLogo from "../../components/AkasLogo";

const AkasBrand = () => (
  <span className="login-akas-brand" aria-label="Akas">
    <AkasLogo className="login-akas-mark" decorative />
    <span>Akas</span>
  </span>
);

const LoginPage = ({ login, isLoading, storeSetting }) => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const inputRefUsername = useRef(null);
  const inputRefPassword = useRef(null);
  const formRef = useRef(null);
  const loginSchema = React.useMemo(() => ({
    username: rule.required("Username atau email wajib diisi."),
    password: rule.required("Password wajib diisi."),
  }), []);
  const validation = useFormValidation({ values: formState, schema: loginSchema, formId: "login", focusRoot: formRef });
  const errorMessage = localStorage.getItem("verivyErrorMessage");
  const store = normalizeStoreSetting(storeSetting);
  const storeName = getStoreName(store, "bisnis Anda");
  const storeLogo = getStoreLoginLogo(store);
  const storeInitials = storeName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  const publicSiteUrl = getPublicSiteUrl();

  const handleLogin = (event) => {
    event.preventDefault();
    if (isLoading || !validation.validateAll()) return;
    login(formState, navigate);
  };

  useEffect(() => {
    inputRefUsername.current?.focus();
  }, []);

  useEffect(() => {
    document.title = `Masuk · ${storeName} | AKAS`;
  }, [storeName]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(decodeURIComponent(errorMessage));
      localStorage.removeItem("verivyErrorMessage");
    }
  }, [errorMessage]);

  const handleForgotPassword = () => {
    toast("Hubungi administrator bisnis Anda untuk mengatur ulang password.", { icon: "ℹ" });
  };

  return (
    <main className="akas-login-page">
      <section className="akas-login-story">
        <div className="akas-login-orb orb-one" />
        <div className="akas-login-orb orb-two" />
        <a className="akas-login-logo" href={publicSiteUrl}>
          <AkasBrand />
        </a>
        <div className="akas-login-workspace">
          {storeLogo ? (
            <img src={storeLogo} alt={`Logo ${storeName}`} />
          ) : (
            <span className="akas-login-workspace-initials">{storeInitials || "A"}</span>
          )}
          <span>
            <small>Workspace aktif</small>
            <strong>{storeName}</strong>
          </span>
        </div>
        <div className="akas-login-story-content">
          <span className="akas-login-eyebrow">
            <i /> Portal operasional {storeName}
          </span>
          <h1 className="text-light">
            Kelola {storeName} dengan ritme kerja yang <em>lebih rapi.</em>
          </h1>
          <p>
            {store.tagline ||
              "Pantau penjualan, kelola stok, dan lihat perkembangan usaha dalam satu ruang kerja yang dibuat khusus untuk tim Anda."}
          </p>
          <div className="akas-login-benefits">
            <span>
              <i>✓</i> Data usaha terpusat
            </span>
            <span>
              <i>✓</i> Insight yang mudah dipahami
            </span>
            <span>
              <i>✓</i> Aman untuk setiap anggota tim
            </span>
          </div>
        </div>
        <div className="akas-login-mini-dashboard" aria-hidden="true">
          <div className="akas-login-mini-top">
            <span>Ringkasan hari ini</span>
            <b>Live</b>
          </div>
          <strong>Rp 8.420.000</strong>
          <div className="akas-login-mini-chart">
            {[38, 52, 46, 70, 60, 84, 96].map((height, index) => (
              <i key={`${height}-${index}`} style={{ height: `${height}%` }} />
            ))}
          </div>
          <div className="akas-login-mini-stats">
            <span>
              128<small>Transaksi</small>
            </span>
            <span>
              294<small>Produk terjual</small>
            </span>
            <span>
              92%<small>Stok aman</small>
            </span>
          </div>
        </div>
        <div className="akas-login-story-footer">© {new Date().getFullYear()} Akas · Dibuat untuk UMKM Indonesia</div>
      </section>

      <section className="akas-login-form-side">
        <ThemeToggle className="akas-login-theme-toggle" showLabel={false} />
        <div className="akas-login-mobile-brand">
          <a href={publicSiteUrl}>
            <AkasBrand />
          </a>
          <strong>{storeName}</strong>
        </div>
        <div className="akas-login-form-wrap">
          <div className="akas-login-heading">
            <span>Selamat datang di {storeName}</span>
            <h2>Masuk ke akun {storeName}</h2>
            <p>Gunakan akun yang diberikan administrator {storeName} untuk melanjutkan pekerjaan.</p>
          </div>

          <form ref={formRef} className="akas-login-form" noValidate onSubmit={handleLogin}>
            <label className="akas-login-field">
              <span>Username atau email</span>
              <div className={`akas-login-input-wrap${validation.errors.username ? " is-invalid" : ""}`}>
                <i aria-hidden="true">@</i>
                <input
                  {...validation.fieldProps("username")}
                  ref={inputRefUsername}
                  type="text"
                  placeholder="contoh: owner@bisnis.com"
                  autoComplete="username"
                  value={formState.username}
                  onChange={(event) => setFormState({ ...formState, username: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") inputRefPassword.current?.focus();
                  }}
                  required
                />
              </div>
              <FieldFeedback formId="login" name="username" error={validation.errors.username} />
            </label>

            <label className="akas-login-field">
              <span>Password</span>
              <div className={`akas-login-input-wrap${validation.errors.password ? " is-invalid" : ""}`}>
                <i aria-hidden="true">••</i>
                <input
                  {...validation.fieldProps("password")}
                  ref={inputRefPassword}
                  type={showPassword ? "text" : "password"}
                  placeholder="Masukkan password"
                  autoComplete="current-password"
                  value={formState.password}
                  onChange={(event) => setFormState({ ...formState, password: event.target.value })}
                  required
                />
                <button
                  type="button"
                  className="akas-show-password"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>
                  {showPassword ? "Sembunyikan" : "Lihat"}
                </button>
              </div>
              <FieldFeedback formId="login" name="password" error={validation.errors.password} />
            </label>

            <div className="akas-login-options">
              <label className="akas-remember">
                <input type="checkbox" />
                <span>Ingat perangkat ini</span>
              </label>
              <button type="button" onClick={handleForgotPassword}>
                Lupa password?
              </button>
            </div>

            <button type="submit" className="akas-login-submit" disabled={isLoading}>
              <span>{isLoading ? "Sedang masuk..." : `Masuk ke ${storeName}`}</span>
              <i>→</i>
            </button>
          </form>

          <div className="akas-login-security">
            <span>✓</span>
            <p>
              <strong>Koneksi terlindungi</strong>
              <small>Informasi akun Anda diproses secara aman.</small>
            </p>
          </div>
          {/* <p className="akas-login-register">
            Belum menggunakan Akas? <a href={`${publicSiteUrl}#harga`}>Lihat paket yang tersedia</a>
          </p> */}
        </div>
        <div className="akas-login-side-footer">
          <a href={publicSiteUrl}>Tentang AKAS</a>
          <span>•</span>
          <button type="button" onClick={handleForgotPassword}>
            Butuh bantuan?
          </button>
        </div>
      </section>
      <OverlayLoading
        isShow={isLoading}
        title="Sedang masuk ke Akas"
        message={`Menyiapkan ruang kerja ${storeName} untuk Anda.`}
      />
    </main>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state?.auth?.login?.loading,
  storeSetting: state?.stores?.setting,
});

export default connect(mapStateToProps, { login })(LoginPage);
